
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace, State, Action } from "vuex-class"
import { BFormInput } from "bootstrap-vue"
import CalculationDataType from "@/types/field"

import { CaseColumn } from "@/types/caseColumns"
import {
	CalculationType,
	DataType,
	DynamicParameter,
	FormattingRule,
	GroupByDateFormat,
	Insight,
	InsightCalculation,
	InsightCalculationTotal,
	InsightDetails,
	InsightSummary,
	insightViews,
	Operator,
	operatorLabels
} from "@/types/insights"

import { groupByOptions, groupingOptions } from "@/types/dataType"

import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import SuggestList from "@/components/Fields/SuggestList.vue"

import ParameterValue from "../Controls/ParameterValue.vue"
import ColumnSelect from "../Controls/ColumnSelect.vue"
import ColumnFormatting from "../Controls/ColumnFormatting.vue"
import InsightFilters from "./InsightFilters.vue"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import DynamicParameters from "./DynamicParameters.vue"
import InsightCalculations from "./InsightCalculations.vue"

import api from "@/services/api"
import Guid from "@/scripts/guid"
import { Field, TimeZone } from "@/types"
import { Folder } from "@/types/folder"
import { TimeSpentCalculation } from "@/types/insights/calculation"
import TimeSpentCalculationInput from "./TimeSpentCalculationInput.vue"
import { DataSource } from "@/types/dataSourceItem"
import { CurrentUser } from "@/types/user"
import DefinitionTitle from "@/components/Config/Definitions/DefinitionTitle.vue"
import { UserRole } from "@/types/caseUser"
import { TenantFeatures } from "@/types/tenantFeatures"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		HelpIcon,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"filters": InsightFilters,
		"columns-select": ColumnSelect,
		"parameter-value": ParameterValue,
		"column-formatting": ColumnFormatting,
		"suggest-list": SuggestList,
		"data-select": DataSourceSelect,
		"time-spent-calculation-input": TimeSpentCalculationInput,
		"dynamic-parameters": DynamicParameters,
		"calculations": InsightCalculations,
		"definition-title": DefinitionTitle
	}
})
export default class InsightParameters extends Vue {
	$refs!: {
		titleInput: BFormInput
	}

	@Prop({ default: null })
	insight!: InsightDetails;

	@Prop({ default: () => [] })
	insights!: InsightSummary[];

	filtersExpanded: boolean = false;
	operators: any[] = operatorLabels;

	@Prop()
	fields!: CaseColumn[];

	@Prop()
	userFields!: CaseColumn[];

	@Prop({ default: () => [] })
	folders!: Folder[]

	@Prop({ default: () => [] })
	globalInsightParameters!: DynamicParameter[]

	@State me!: CurrentUser;

	private timezones: TimeZone[] = []

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	@setupStore.State dataSources!: DataSource[]

	@Getter("getTenantSettings") tenantSettings!: { [ key: string]: any }
	@Action("loadTenantSettings") loadTenantSettings!: () => Promise<void>

	get visible (): boolean {
		return !!this.editFormattingColumn
	}
	set visible (value: boolean) {
		if (!value) {
			this.editFormattingCalculation = null
			this.$emit("hide")
		}
	}

	cachedFetchedInsights: { [key: string]: Insight | null } = {}
	private fetchingInsightIds: Set<string> = new Set<string>()

	calculationsValid: boolean = true;
	filtersValid: boolean = true;

	private Operator = Operator;

	loaded: boolean = false;

	editFormattingCalculation: InsightCalculation | InsightCalculationTotal | null = null

	ForecastByFields: string[] = []

	groupBySubRole: string | null = null

	queryColumns: any[] = [
		{
			key: "field",
			label: "Field"
		},
		{
			key: "operator",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	calculationTypeValues: any[] = [
		{
			type: CalculationType.Total,
			label: "Total",
			requiresField: true
		},
		{
			type: CalculationType.Count,
			label: "Count",
			requiresField: false
		},
		{
			type: CalculationType.Average,
			label: "Average",
			requiresField: true
		},
		{
			type: CalculationType.Min,
			label: "Minimum",
			requiresField: true
		},
		{
			type: CalculationType.Max,
			label: "Maximum",
			requiresField: true
		},
		{
			type: CalculationType.CountPerDay,
			label: "Count Per Day",
			requiresField: false,
			canUse: () => this.insight.clauses.some(c => c.fieldDataType === DataType.dateTime && c.operator === Operator.GreaterThan)
		}
	]

	public get userCanEditInsights (): boolean {
		return !!this.me && !!this.me.editInsights
	}

	public get showAdvancedButtons (): boolean {
		return this.userCanEditInsights && this.filtersExpanded && !!this.insight.id
	}

	get calculationTypes (): any[] {
		return this.calculationTypeValues.filter(calcType => calcType.canUse === undefined || calcType.canUse())
	}

	groupByDateOptions: {value: GroupByDateFormat, label: string}[] = [
		{ value: GroupByDateFormat.Day, label: "Day" },
		{ value: GroupByDateFormat.Week, label: "Week" },
		{ value: GroupByDateFormat.Month, label: "Month" },
		{ value: GroupByDateFormat.Quarter, label: "Quarter" }
	]

	get groupByFields (): any[] {
		if (!this.fields) {
			return []
		}

		const groupableDataTypes = ["number", "date", "date-time", "list", "list-multiple", "checkbox", "user", "calculated", "user-role"]
		return this.fields.filter((f: any) => !!f && (groupableDataTypes.indexOf(f.dataType) > -1 ||
			(f.dataType === "list-data" && f.dataSourceId)))
	}

	get selectedGroupByField (): any {
		return this.groupByFields.find(f => f.key === this.insight.groupBy)
	}

	get groupByFieldSubroles (): {title: string, value: string, }[] {
		var fieldSubroles: UserRole[] = this.selectedGroupByField.fieldExtensions!.subroles!

		if (fieldSubroles) {
			return fieldSubroles.map(subrole => {
				return {
					title: subrole.title,
					value: subrole.title
				}
			})
		}
		return []
	}

	get groupByOptions (): {value: string, label: string}[] {
		const field = this.fields.find(f => f.key === this.insight.groupBy || "fields." + f.key === this.insight.groupBy)
		if (!field || !field.fieldDataType || !groupByOptions[field.fieldDataType]) {
			return []
		}

		return groupByOptions[field.fieldDataType]!
	}

	get sortByFields (): any[] {
		if (!this.fields) {
			return []
		}

		const sortbleDataTypes = ["number", "date", "date-time", "checkbox", "user-role", "calculated"]
		return this.fields.filter((f: any) => !!f && sortbleDataTypes.includes(f.dataType))
	}

	setCalculationsValid (calculationValid: boolean) {
		this.calculationsValid = calculationValid
	}

	private stripFieldId (fieldId: string) : string {
		if (fieldId.startsWith("fields.")) {
			return fieldId.substring(7)
		}
		return fieldId
	}

	private getFieldFromFieldId (fieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(fieldId)
		const field = this.fields.find(c => c.fieldId === strippedFieldId)
		if (!field) {
			Error(`Field ${fieldId} not found`)
		}
		return field!
	}

	private getTimeSpentTrackingField (timeSpentFieldId: string) : CaseColumn {
		const strippedFieldId = this.stripFieldId(timeSpentFieldId)
		const timeSpentField = this.fields.find(c => c.fieldId === strippedFieldId)

		const trackingFieldId = timeSpentField!.fieldExtensions!.trackingFieldId
		const trackingField = this.fields.find(c => c.fieldId === trackingFieldId)

		if (!trackingField) {
			throw new Error(`Could not find tracking field ${trackingFieldId}`)
		}
		return trackingField
	}

	private timeSpentCalculationChange (
		calculation: InsightCalculation,
		timeSpentCalculation: TimeSpentCalculation) {
		calculation.timeSpentCalculation = timeSpentCalculation
	}

	private getCalculationDataType (field: CaseColumn) : string | null {
		if (!field.fieldExtensions) {
			return null
		}
		const calculationDataType = CalculationDataType[field.fieldExtensions.calculationType as CalculationType]
		return calculationDataType
	}

	get editFormattingColumn (): CaseColumn {
		return this.editFormattingCalculation
			? {
				label: (this.editFormattingCalculation as InsightCalculation).label || (this.editFormattingCalculation as InsightCalculationTotal).title || "Untitled",
				dataType: "number",
				fieldDataType: DataType.number
			} as any
			: null
	}

	get editFormattingRules (): FormattingRule[] {
		return this.editFormattingCalculation && this.editFormattingCalculation.formattingRules
			? this.editFormattingCalculation.formattingRules
			: []
	}

	get calculationNumberColumns () {
		return this.insight.calculations.map(c => {
			return {
				label: c.label,
				key: `calculations[${c.key}]`,
				fieldDataType: DataType.number
			}
		})
	}

	private get avaliableViews () {
		var calculations = this.insight.calculations

		if (this.insight.includeCalculationsFrom) {
			this.insight.includeCalculationsFrom.forEach((insightId: string) => {
				const insight = this.getInsightDetails(insightId)
				if (insight) {
					calculations = [
						...calculations,
						...insight.calculations
					]
				}
			})
		}

		var avaliableViews = insightViews.filter(display => display.canDisplay(this.insight, calculations, this.fields, false))

		// clear the default view if it is not avaliable
		if (!avaliableViews.find(v => v.key === this.insight.defaultView)) {
			this.insight.defaultView = null
		}
		return avaliableViews
	}

	private get includedParameters () : DynamicParameter[] {
		return this.insight && this.insight.includeCalculationsFrom
			? this.insight.includeCalculationsFrom
				.map(insightId => {
					const insight = this.getInsightDetails(insightId)
					return insight && insight.dynamicParameters ? insight.dynamicParameters : []
				})
				.flat()
			: []
	}

	private get forecastingCheckboxEnabled () : boolean {
		if (!this.isForecastingFeatureEnabled) {
			return false
		}
		const hasGreaterAndLessThanDates = this.availableForecastByFields.length > 0

		const hasDateRange = this.insight.clauses.some(c => c.operator === Operator.DateRange && c.fieldDataType === DataType.dateTime)

		if (!hasGreaterAndLessThanDates && !hasDateRange) { // force forecasting to be false when not allowed
			this.$set(this.insight, "includeForecast", false)
			this.$set(this.insight, "forecastBy", null)
		}

		return hasGreaterAndLessThanDates || hasDateRange
	}

	async mounted () {
		if (this.insight) {
			this.updateInsightValues()

			if (this.insight.id) {
				this.runInsight()
			} else {
				this.filtersExpanded = true
			}
			if (!this.insight.title) {
				this.insight.title = ""
			}
		}

		// Load available timezones.
		this.timezones = await api.timeZonesGet()
		await this.loadTenantSettings()
		if (this.tenantSettings && this.tenantSettings["timeZones"] && Array.isArray(this.tenantSettings["timeZones"]) && this.tenantSettings["timeZones"].length > 0) {
			this.timezones = this.timezones.filter(tz => this.tenantSettings["timeZones"].includes(tz.id))
		}

		this.loaded = true

		if (this.insight && !this.insight.id) {
			this.focusTitle()
		}
	}

	@Watch("insight")
	onInsightChanged () {
		this.updateInsightValues()

		if (!this.insight.id) {
			this.filtersExpanded = true
			this.focusTitle()
		} else if (!this.filtersExpanded) {
			this.runInsight()
		}
	}

	private focusTitle (): void {
		this.$nextTick(() => {
			if (this.$refs.titleInput) {
				this.$refs.titleInput.focus()
			}
		})
	}

	updateInsightValues () {
		// Add forecast by field to existing forecasted insights
		if (this.forecastingCheckboxEnabled && !this.insight.forecastBy && this.availableForecastByFields.find(f => f.key === "created")) {
			this.insight.forecastBy = "created"
		}

		// Assign groupBySubRole from the insight for the v-select to use
		if (this.showCalculatedUserStatus && this.insight.groupingOptions && this.insight.groupingOptions[groupingOptions.subRole]) {
			this.groupBySubRole = this.insight.groupingOptions[groupingOptions.subRole]
		}

		if (this.insight.includeCalculationsFrom) {
			this.insight.includeCalculationsFrom.forEach(insightId => {
				this.getInsightDetails(insightId)
			})
		}
	}

	@Watch("filtersExpanded")
	onFiltersExpandedChanged (newValue: boolean) {
		this.$emit("filtersExpanded", newValue)
	}

	@Watch("groupBySubRole")
	onGroupBySubRole () {
		this.insight.groupingOptions = {}
		if (this.groupBySubRole) {
			this.insight.groupingOptions[groupingOptions.subRole] = this.groupBySubRole
		}
	}

	setInsightData () {
		if (this.insight.groupBy) {
			if (this.selectedGroupByField) {
				this.insight.groupByDataType = this.selectedGroupByField.fieldDataType
			} else {
				this.insight.groupByDataType = null
			}
		}

		if (this.insight.sortBy) {
			const sortByField = this.sortByFields.find(f => f.key === this.insight.sortBy)
			if (sortByField) {
				this.insight.sortByDataType = sortByField.fieldDataType
			} else {
				this.insight.sortByDataType = null
			}
		}

		this.insight.clauses.forEach((field: any) => {
			if (!field.orClause) {
				field.field = field.fieldData ? field.fieldData.key : null
				field.fieldDataType = field.fieldData && field.fieldData.comparisonType !== undefined && field.fieldData.comparisonType !== null
					? field.fieldData.comparisonType
					: field.fieldData ? field.fieldData.fieldDataType : null
			}
		})

		this.insight.displayColumns = this.insight.columns.map((c: string) =>
			this.fields.find(f => f.key === c || "fields." + f.key === c)!)
	}

	runInsight () {
		this.setInsightData()
		this.filtersExpanded = false

		this.$emit("runInsight", this.insight)
	}

	saveInsight () {
		this.setInsightData()
		this.$emit("saveInsight", this.insight)
	}

	deleteInsight () {
		this.$emit("deleteInsight", this.insight)
	}

	duplicateInsight () {
		this.$emit("duplicateInsight", this.insight)
	}

	isCalcValid (calculation: any): boolean {
		if (!calculation.label ||
			(calculation.calcType === undefined || calculation.calcType === null) ||
			(calculation.calcType !== CalculationType.Count && calculation.calcType !== CalculationType.CountPerDay && !calculation.field)
		) {
			return false
		}
		return true
	}

	@Watch("insight.calculations", { deep: true })
	calcWatcher () {
		if (this.insight.calculations &&
				this.insight.calculations.map((calc: any) => this.isCalcValid(calc)).includes(false) &&
				this.insightsCalculationTotalsValid()) {
			this.calculationsValid = false
		} else {
			this.calculationsValid = true
		}
	}

	@Watch("insight.groupBy", { deep: true })
	groupByWatcher () {
		if (!this.groupByDate) {
			this.insight.groupByDateFormat = null
		}
		this.groupByDateFormatWatcher()
		this.groupByOptionWatcher()
	}

	@Watch("insight.groupByDateFormat", { deep: true })
	groupByDateFormatWatcher () {
		if (!this.showCalculatedDateRange) {
			this.insight.showCalculatedDateRange = null
		}
	}

	@Watch("insight.groupByOption", { deep: true })
	groupByOptionWatcher () {
		if (!this.showCalculatedUserStatus) {
			this.insight.showCalculatedUserStatus = null
		}
	}

	addCalculation () {
		if (this.insight && !this.insight.calculations) {
			this.insight.calculations = []
		}
		if (this.insight.calculations && (this.insight.calculations.length === 0 || this.calculationsValid)) {
			this.insight.calculations.push({
				fieldId: "",
				key: Guid.newGuid(),
				label: "",
				formattingRules: []
			})
		}
	}

	removeCalculation (calculation: InsightCalculation) {
		this.insight.calculations.splice(this.insight.calculations.indexOf(calculation), 1)

		// Remove calculation from any calculation totals
		if (this.insight.calculationTotals && this.insight.calculationTotals.length > 0) {
			this.insight.calculationTotals.forEach(c => {
				c.calculationList.remove(calculation.key)
			})
		}
	}

	showCalculationFormattingRules (calculation: InsightCalculation | InsightCalculationTotal | null) {
		this.editFormattingCalculation = calculation
	}

	setFormattingRules (rules: FormattingRule[]) {
		if (this.editFormattingCalculation) {
			Vue.set(this.editFormattingCalculation!, "formattingRules", rules)
		}
	}

	/**
	 * Returns cached insight details for the given ID, or null if not loaded yet.
	 * If the data isn't cached and not currently fetching, triggers an API request
	 * to load and cache the insight details. Prevents duplicate fetches for the same ID.
	 *
	 * @param {string} insightId - The ID of the insight to retrieve.
	 * @returns {object|null} The cached insight details or null while loading.
	 */
	getInsightDetails (insightId: string) {
		if (!this.cachedFetchedInsights[insightId] && !this.fetchingInsightIds.has(insightId)) {
			Vue.set(this.cachedFetchedInsights, insightId, null)
			this.fetchingInsightIds.add(insightId)
			api.insightGet(insightId).then(insightDetails => {
				Vue.set(this.cachedFetchedInsights, insightId, insightDetails)
			}).finally(() => this.fetchingInsightIds.delete(insightId))
		}

		return this.cachedFetchedInsights[insightId]
	}

	private get groupByDate (): boolean {
		const field = this.fields.find(f => f.key === this.insight.groupBy || "fields." + f.key === this.insight.groupBy)
		if (field) {
			return field.dataType === "date" || field.dataType === "date-time" || field.fieldDataType === DataType.dateTime
		}
		return false
	}

	// Check to show/hide the "show date range on calculation table" option when grouping by dates in a weekly format.
	private get showCalculatedDateRange () {
		const field = this.fields.find(f => f.key === this.insight.groupBy || "fields." + f.key === this.insight.groupBy)
		if (field && (field.dataType === "date" || field.dataType === "date-time" || field.fieldDataType === DataType.dateTime) && this.insight.groupByDateFormat === GroupByDateFormat.Week) {
			if (this.insight.showCalculatedDateRange === null) {
				this.insight.showCalculatedDateRange = false
			}
			return true
		}
		return false
	}

	// Check to show/hide the "show user status" option when grouping by user role.
	private get showCalculatedUserStatus () {
		const field = this.fields.find(f => f.key === this.insight.groupBy || "fields." + f.key === this.insight.groupBy)
		if (field && (field.dataType === "user-role" || field.fieldDataType === DataType.userRole) && this.insight.groupByOption === "user") {
			if (this.insight.showCalculatedUserStatus === null) {
				this.insight.showCalculatedUserStatus = false
			}
			return true
		}
		return false
	}

	private get insightsTotalFields (): InsightCalculation[] {
		if (this.insight && this.insight.calculations) {
			return this.insight.calculations.filter((c: { label: string }) => c.label)
		}
		return []
	}

	/**
	 * Used to specify when insights calculation totals should be shown.
	 * @returns true if there is at least 1 calculation total, false otherwise.
	 */
	private get insightsTotalShown (): boolean {
		return this.insightsTotalFields.length > 0
	}

	/**
	 * Checks that the insight calculation totals are valid.
	 * @returns True if the list of calculation totals are filled in (i.e. have a title and >1 calculation). False otherwise.
	 * @private
	 */
	private insightsCalculationTotalsValid (): boolean {
		if (this.insight.calculationTotals) {
			return !this.insight.calculationTotals.some((t: InsightCalculationTotal) => !t.title || t.calculationList.length === 0 || t.expression !== "")
		}
		return true
	}

	private checkCalculationValidity () {
		this.insight.calculations.filter(calculation => !this.calculationTypes.find(calcType => calcType.type === calculation.calcType))
			.forEach(calculation => calculation.calcType = null)
	}

	get forecastingValid () : boolean {
		if (!this.isForecastingFeatureEnabled || !this.insight.includeForecast) {
			return true
		}

		var forecastByValid = false
		if (this.insight.forecastBy) {
			// Check that the current ForecastBy field is still avaliable
			forecastByValid = this.availableForecastByFields.filter(f => f.key === this.insight.forecastBy).length !== 0

			// Clear forecastBy if the current ForecastBy is no longer valid
			if (!forecastByValid) {
				this.insight.forecastBy = null
			}
		}
		return forecastByValid
	}

	private get isForecastingFeatureEnabled () : boolean {
		return this.isFeatureEnabled(TenantFeatures.Forecasting)
	}

	get availableForecastByFields (): any[] {
		var lessThanClauses = this.insight.clauses.filter(lessThanClause => lessThanClause.operator === Operator.LessThan && lessThanClause.fieldDataType === DataType.dateTime)
		var greaterThanClauses = this.insight.clauses.filter(greaterThanClause => greaterThanClause.operator === Operator.GreaterThan && greaterThanClause.fieldDataType === DataType.dateTime)
		var matchingClauses = lessThanClauses.filter(lessThanClause => greaterThanClauses.filter(greaterThanClause => lessThanClause.field === greaterThanClause.field))

		var DateRangeClauses = this.insight.clauses.filter(c => c.operator === Operator.DateRange && c.fieldDataType === DataType.dateTime)

		// Prevent forecasting by Closed and Last Updated
		var forecastByFields = matchingClauses.concat(DateRangeClauses).filter(c => c.field !== "closed" && c.field !== "lastUpdated")

		return forecastByFields.map(c => {
			return { key: c.field!, label: c.fieldData!.label }
		})
	}

	private get availableDynamicParameters () {
		var dynamicParameters: DynamicParameter[] = []

		if (this.insight.dynamicParameters) {
			dynamicParameters = this.insight.dynamicParameters
		}

		if (this.insight.includeGlobalParameters) {
			this.insight.includeGlobalParameters.forEach(id => {
				const parameters = this.globalInsightParameters.filter(parameter => parameter.key === id)
				dynamicParameters = dynamicParameters.concat(parameters)
			})
		}

		return dynamicParameters
	}

	private checkIfShowGroupByTimePeriod () {
		if (this.insight.groupByDateFormat === GroupByDateFormat.Week) {
			this.showGroupByTimePeriod = true
			return
		}
		this.showGroupByTimePeriod = false
	}

	showGroupByTimePeriod = false
}

